import { equalsIgnoringCase } from "./string";

export async function copyTextToClipboard(text: string) {
  try {
    if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
      try {
        await navigator.clipboard.writeText(text);
      } catch {
        // clipboard.writeText may still throw if copying from inside iframe
        // fallback to legacy method
        copyToClipboardLegacy(text);
      }
    } else {
      copyToClipboardLegacy(text);
    }

    return true;
  } catch (ex) {
    console.warn("Copy to clipboard failed.", ex);
    return false;
  }
}

function copyToClipboardLegacy(text: string) {
  let textarea = null;
  let selection = null;

  try {
    textarea = document.createElement("textarea");
    textarea.textContent = text;
    textarea.style.position = "fixed";
    textarea.setAttribute("readonly", "");
    document.body.appendChild(textarea);

    selection = document.getSelection();

    if (selection) {
      const range = document.createRange();
      range.selectNode(textarea);
      selection.removeAllRanges();
      selection.addRange(range);
    } else {
      textarea.focus();
      textarea.select();
    }

    return document.execCommand("copy");
  } finally {
    if (selection) {
      selection.removeAllRanges();
    }

    if (textarea) {
      document.body.removeChild(textarea);
    }
  }
}

export function addEventListeners(el: EventTarget, events: string[], handler: EventListener) {
  for (let i = 0, len = events.length; i < len; i++) {
    el.addEventListener(events[i], handler);
  }
}

export function removeEventListeners(el: EventTarget, events: string[], handler: EventListener) {
  for (let i = 0, len = events.length; i < len; i++) {
    el.removeEventListener(events[i], handler);
  }
}

export function testSameOrigin(first: string, second: string) {
  const a = document.createElement("a");
  const b = document.createElement("a");

  a.href = first;
  b.href = second;
  return a.hostname === b.hostname && a.port === b.port && a.protocol === b.protocol;
}

export const KeyCodes = {
  esc: 27,
  tab: 9,
  enter: 13,
  space: 32,
  comma: 188,
  up: 38,
  left: 37,
  right: 39,
  down: 40,
  backspace: 8,
  delete: [8, 46],
  a: 65,
  colon: 186,
};

export type KeyCodeKeys = keyof typeof KeyCodes;

export const Keys: { [k in KeyCodeKeys]: string } = {
  esc: "Escape",
  tab: "Tab",
  enter: "Enter",
  space: " ",
  comma: ",",
  up: "ArrowUp",
  left: "ArrowLeft",
  right: "ArrowRight",
  down: "ArrowDown",
  backspace: "Backspace",
  delete: "Delete",
  a: "a",
  colon: ":",
};

export function isKeyboardKey(event: KeyboardEvent, code: KeyCodeKeys): event is KeyboardEvent {
  if (!(event instanceof KeyboardEvent)) return false;

  const keyCode = KeyCodes[code];
  if (keyCode) {
    if (Array.isArray(keyCode)) {
      if (keyCode.includes(event.keyCode)) return true;
    }
    if (event.keyCode === keyCode) {
      return true;
    }
  }
  return equalsIgnoringCase(event.key, Keys[code]);
}

export function isIframe() {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

export function isScrolledIntoView(el: HTMLElement, options: { allowPartial?: boolean } = { allowPartial: false }) {
  const rect = el.getBoundingClientRect();
  const elemTop = rect.top;
  const elemBottom = rect.bottom;

  if (options.allowPartial) {
    const isVisible = elemTop < window.innerHeight && elemBottom >= 0;
    return isVisible;
  }

  const isVisible = (elemTop >= 0) && (elemBottom <= window.innerHeight);
  return isVisible;
}

export function getScrollParent(node: Element) {
  if (!node) return;

  if (node.scrollHeight > node.clientHeight) {
    return node;
  }

  if (node.parentNode instanceof Element) {
    return getScrollParent(node.parentNode);
  }

  return undefined;
}
